import { Input } from "@legal-500/legal500-design-system";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { CmsImageUploadField } from "../../../components/CMS/CmsImageUploadField/CmsImageUploadField";
import {
  BackgroundField,
  BlockItemList,
  RichTextField,
  useSyncBlockContent,
  useUploadTracker,
} from "./blockEditorParts";
import type { GalleryContent } from "./types";
import styles from "../CMS.module.css";

type GalleryEditorProps = {
  value: GalleryContent;
  onChange: (value: GalleryContent) => void;
  onUploadingChange?: (uploading: boolean) => void;
};

export function GalleryEditor({ value, onChange, onUploadingChange }: GalleryEditorProps) {
  const { control, register, watch } = useForm<GalleryContent>({ defaultValues: value });
  const { fields, append, remove, move } = useFieldArray({ control, name: "images" });
  const { uploading, report } = useUploadTracker(onUploadingChange);
  useSyncBlockContent(watch, value, onChange);

  return (
    <div className={styles.cardEditorFields}>
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="gallery-heading">
          Heading
        </label>
        <Input id="gallery-heading" {...register("heading")} />
      </div>
      <RichTextField control={control} name="intro" id="gallery-intro" label="Intro" />

      <BlockItemList
        noun="Image"
        keys={fields.map(field => field.id)}
        disabled={uploading}
        onMove={(from, to) => move(from, to)}
        onRemove={remove}
        onAdd={() => append({ url: "", alt: "", caption: "" })}
        renderItem={(index, key) => (
          <>
            <div className={styles.formField}>
              <Controller
                name={`images.${index}.url`}
                control={control}
                render={({ field }) => (
                  <CmsImageUploadField
                    noun="image"
                    optional={false}
                    recommendation="Recommended: 1200 pixels wide or larger."
                    previewAlt="Gallery image"
                    value={field.value}
                    cardLabel={`Image ${index + 1}`}
                    onChange={field.onChange}
                    onUploadingChange={pending => report(key, pending)}
                  />
                )}
              />
            </div>
            <div className={styles.cardEditorFieldRow}>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`gallery-alt-${key}`}>
                  Image description (alt text)
                  {watch(`images.${index}.url`) && <span className={styles.required}> *</span>}
                </label>
                <Input
                  id={`gallery-alt-${key}`}
                  required={Boolean(watch(`images.${index}.url`))}
                  {...register(`images.${index}.alt`)}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor={`gallery-caption-${key}`}>
                  Caption (optional)
                </label>
                <Input id={`gallery-caption-${key}`} {...register(`images.${index}.caption`)} />
              </div>
            </div>
          </>
        )}
      />

      <BackgroundField register={register} name="background" stored={value.background} />
    </div>
  );
}
